/**
 * tooltip.js
 */

(function () {
  'use strict';

  // ── Config ───────────────────────────────────────────────────────────────
  const ARROW_H              = 8;    // px — must match CSS border value
  const GAP                  = 6;    // px — gap between button edge and arrow tip
  const EDGE_PADDING         = 8;    // px — min distance from viewport edges
  const ARROW_CORNER_BUFFER  = 16;   // px — keeps arrow clear of rounded corners
  const REPOSITION_ON_SCROLL = true; // false = close on scroll instead

  const VALID_BOX_CLASS = 'tooltip__box';

  // ── State ────────────────────────────────────────────────────────────────
  let activeButton  = null;
  let activeBox     = null;
  let rafPending    = false; // rAF gate flag

  // ── Security: validate the tooltip box ───────────────────────────────────
  function isValidBox(el) {
    return (
      el instanceof HTMLElement &&
      el.classList.contains(VALID_BOX_CLASS) &&
      document.contains(el)
    );
  }

  // ── Arrow: create once, cache on the box element ─────────────────────────
  function ensureArrow(box) {
    if (!box._arrow) {
      const arrow = document.createElement('span');
      arrow.className = 'tooltip__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      box.insertBefore(arrow, box.firstChild);
      box._arrow = arrow; // cache — avoids querySelector on every reposition
    }
    return box._arrow;
  }

  // ── Positioning ──────────────────────────────────────────────────────────
  function positionBox(button, box) {
    const arrow = ensureArrow(box);

    // Reset so getBoundingClientRect reflects natural (unconstrained) size
    box.style.left = '0px';
    box.style.top  = '0px';

    const btn = button.getBoundingClientRect();
    const bx  = box.getBoundingClientRect();
    const vpW = window.innerWidth;
    const vpH = window.innerHeight;

    // Vertical — prefer below, flip above only if more room there
    const needsAbove = (vpH - btn.bottom) < (bx.height + GAP + ARROW_H)
                    && btn.top > (vpH - btn.bottom);

    box.classList.toggle('is-above', needsAbove);

    const boxTop = needsAbove
      ? btn.top    - GAP - ARROW_H - bx.height
      : btn.bottom + GAP + ARROW_H;

    // Horizontal — centre on button midpoint, clamp to viewport
    const btnMidX = btn.left + btn.width / 2;
    const boxLeft = Math.max(
      EDGE_PADDING,
      Math.min(btnMidX - bx.width / 2, vpW - bx.width - EDGE_PADDING)
    );

    // Arrow — offset within box that aligns with button midpoint
    const arrowLeft = Math.max(
      ARROW_CORNER_BUFFER,
      Math.min(btnMidX - boxLeft, bx.width - ARROW_CORNER_BUFFER)
    );

    box.style.left   = boxLeft   + 'px';
    box.style.top    = boxTop    + 'px';
    arrow.style.left = arrowLeft + 'px';
  }

  // ── Open / close ─────────────────────────────────────────────────────────
  function openTooltip(button, box) {
    if (activeButton && activeButton !== button) {
      closeTooltip(activeButton, activeBox, false);
    }

    activeButton = button;
    activeBox    = box;

    if (box.parentElement !== document.body) {
      document.body.appendChild(box);
    }

    box.classList.add('is-visible');
    button.setAttribute('aria-expanded', 'true');
    positionBox(button, box);
  }

  function closeTooltip(button, box, returnFocus) {
    box.classList.remove('is-visible', 'is-above');
    button.setAttribute('aria-expanded', 'false');
    if (returnFocus !== false) button.focus();
    activeButton = null;
    activeBox    = null;
  }

  // ── Events ───────────────────────────────────────────────────────────────
  function onButtonClick(e) {
    const button = e.currentTarget;
    const boxId  = button.getAttribute('aria-controls');
    if (!boxId) return;

    const box = document.getElementById(boxId);
    if (!isValidBox(box)) {
      console.warn('[Tooltip] aria-controls="' + boxId + '" does not point to a valid tooltip box.');
      return;
    }

    button.getAttribute('aria-expanded') === 'true'
      ? closeTooltip(button, box)
      : openTooltip(button, box);
  }

  function onDocumentClick(e) {
    if (!activeButton) return;
    if (!activeButton.contains(e.target) && !activeBox.contains(e.target)) {
      closeTooltip(activeButton, activeBox, false);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Escape' && activeButton) {
      closeTooltip(activeButton, activeBox);
    }
  }

  // rAF-gated scroll/resize handler — fires at most once per animation frame
  function onScrollOrResize() {
    if (!activeButton || rafPending) return;
    rafPending = true;
    requestAnimationFrame(function () {
      rafPending = false;
      if (!activeButton) return; // tooltip may have closed during the frame
      if (REPOSITION_ON_SCROLL) {
        positionBox(activeButton, activeBox);
      } else {
        closeTooltip(activeButton, activeBox, false);
      }
    });
  }

  // ── Init ─────────────────────────────────────────────────────────────────
  function initButton(button) {
    if (!(button instanceof HTMLElement) || button.dataset.tooltipBound) return;
    button.dataset.tooltipBound = '1';
    button.addEventListener('click', onButtonClick);
  }

  function init() {
    document.querySelectorAll('.tooltip__toggle-button').forEach(initButton);

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (!(node instanceof HTMLElement)) return;
          if (node.matches('.tooltip__toggle-button')) initButton(node);
          node.querySelectorAll('.tooltip__toggle-button').forEach(initButton);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('click',   onDocumentClick);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize',    onScrollOrResize, { passive: true });
    window.addEventListener('scroll',    onScrollOrResize, { passive: true });
    document.addEventListener('scroll',  onScrollOrResize, { passive: true, capture: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();



// WEBPACK FOOTER //
// ./project/client/src/core/modules/tooltip/tooltip.js